
import { Component, Vue } from 'vue-property-decorator'
import { Getter } from 'vuex-class'
import BaseLayout from '@/shared/components/BaseLayout.vue'
import { getAccountId, getCampaignId } from '@/service/storage'
import { RootStateMutations } from '@/store/mutations'
import ExportLogService, { ExportLogItem, ExportLogQueryParams } from '@/api/services/ExportLogService'

type ViewState = 'loading' | 'error' | 'empty' | 'no-results' | 'filled'
type SortDirection = 'descending' | 'ascending' | 'neutral'
const exportLogService = new ExportLogService()

@Component({
    components: { BaseLayout }
})
export default class OfferIssues extends Vue {
    @Getter('currentAccount') account!: number | undefined
    @Getter('currentCampaign') campaign!: number | undefined

    selectedEntityType = ''
    selectedLevel = ''
    dateFrom = ''
    dateTo = ''
    search = ''
    pageSize = 20
    currentPage = 1
    sortDirection: SortDirection = 'descending'
    readonly pageSizes = [10, 20, 50, 100]
    items: ExportLogItem[] = []
    total = 0
    totalPages = 0
    state: ViewState = 'loading'
    requestSequence = 0

    get displayTotalPages (): number { return Math.max(1, this.totalPages) }

    get hasActiveFilters (): boolean {
        return Boolean(this.selectedEntityType.trim() || this.selectedLevel || this.dateFrom || this.dateTo || this.search.trim())
    }

    get paginationRange (): string {
        const start = this.total ? (this.currentPage - 1) * this.pageSize + 1 : 0
        const end = Math.min(this.currentPage * this.pageSize, this.total)
        return this.$t('offerIssues.pagination.range', { start, end, total: this.total }) as string
    }

    get query (): ExportLogQueryParams {
        const query: ExportLogQueryParams = {
            page: this.currentPage,
            pageSize: this.pageSize,
            sort: this.sortDirection === 'ascending' ? 'asc' : 'desc'
        }
        if (this.selectedEntityType.trim()) query.entityType = this.selectedEntityType.trim()
        if (this.selectedLevel) query.severity = this.selectedLevel
        if (this.dateFrom) query.dateFrom = this.dateFrom
        if (this.dateTo) query.dateTo = this.dateTo
        if (this.search.trim()) query.search = this.search.trim()
        return query
    }

    created (): void {
        if (!this.account) this.$store.commit(RootStateMutations.setAccount, getAccountId())
        if (!this.campaign) this.$store.commit(RootStateMutations.setCampaign, getCampaignId())
        this.loadPage()
    }

    onSelectionChanged (): void {
        this.currentPage = 1
        this.loadPage()
    }

    onPageSizeChanged (): void {
        this.currentPage = 1
        this.loadPage()
    }

    changePage (page: number): void {
        const nextPage = Math.min(this.displayTotalPages, Math.max(1, page))
        if (nextPage === this.currentPage) return
        this.currentPage = nextPage
        this.loadPage()
    }

    cycleSortDirection (): void {
        this.sortDirection = this.sortDirection === 'descending' ? 'ascending' : this.sortDirection === 'ascending' ? 'neutral' : 'descending'
        this.currentPage = 1
        this.loadPage()
    }

    formatDate (value: string): string {
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return value
        return date.toLocaleString('ru-RU', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        })
    }

    entityTypeLabel (entityType: string): string {
        if (entityType === 'offer') return this.$t('offerIssues.filters.offerType') as string
        if (entityType === 'order_import') return this.$t('offerIssues.filters.orderImportType') as string
        return entityType
    }

    private async loadPage (): Promise<void> {
        const requestId = ++this.requestSequence
        const accountId = this.account || getAccountId()
        const campaignId = this.campaign || getCampaignId()
        this.state = 'loading'
        this.items = []

        try {
            const result = await exportLogService.getPage(accountId, campaignId, { ...this.query })
            if (requestId !== this.requestSequence) return
            this.items = result.items
            this.total = result.total
            this.totalPages = result.totalPages
            this.state = result.items.length > 0 ? 'filled' : this.hasActiveFilters ? 'no-results' : 'empty'
        } catch (_) {
            if (requestId !== this.requestSequence) return
            this.items = []
            this.total = 0
            this.totalPages = 0
            this.state = 'error'
        }
    }
}
